import React, {forwardRef} from 'react';
import {TableFunctions} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/Table.type';
import {useIntl} from 'react-intl';
import {Table, NumberField} from '@forecast-it/design-system';
import {EyeOptionMap, Company, ExpenseRow, BaselineItem} from './BaselineTypes';

type ExpensesTableProps = {
	editMode?: boolean;
	onDeleteRow: (row: ExpenseRow) => void;
	onCategorySelect: (categoryId: string, row: ExpenseRow) => void;
	company: Company;
	ref: React.MutableRefObject<TableFunctions>;
	recalculateRow?: (row: ExpenseRow, changedField: string) => void;
	setTableData?: React.Dispatch<React.SetStateAction<BaselineItem[]>>;
	tableData: ExpenseRow[];
	eyeOptionMap?: EyeOptionMap;
};

export const ExpensesEstimatesTable = forwardRef<TableFunctions, ExpensesTableProps>((props, ref) => {
	const {editMode, onDeleteRow, company, onCategorySelect, recalculateRow, tableData, setTableData, eyeOptionMap} = props;

	const {formatMessage} = useIntl();

	const getExpenseCategoryName = (row: ExpenseRow) => {
		const category = company?.expenseCategories?.edges?.find(edge => edge?.node?.id === row.categoryId);
		if (category && category.node && category.node.name) {
			return category.node.name;
		}
		return formatMessage({id: 'deliverable.select_expense_category'});
	};

	const currency = company.currency || '';

	return (
		<Table width={'100%'} tableRef={ref} setTableData={setTableData} data={tableData}>
			{editMode ? (
				<Table.TextInputColumn
					accessorKey={'name'}
					header={formatMessage({id: 'deliverable.columns.name'})}
					onInputChange={recalculateRow}
					width="14%"
				/>
			) : (
				<Table.TextColumn accessorKey={'name'} header={formatMessage({id: 'deliverable.columns.name'})} ellipsis />
			)}
			{editMode ? (
				<Table.DropdownColumn
					header={formatMessage({id: 'deliverable.columns.category'})}
					label={getExpenseCategoryName}
					width="14%"
				>
					{(company?.expenseCategories?.edges || [])?.map(category => (
						<Table.DropdownColumn.ListItem
							onClick={row => {
								category?.node?.id && onCategorySelect(category?.node.id, row);
							}}
						>
							{category?.node?.name}
						</Table.DropdownColumn.ListItem>
					))}
				</Table.DropdownColumn>
			) : (
				<Table.TextColumn accessorKey={'categoryName'} header={formatMessage({id: 'deliverable.columns.category'})} />
			)}
			{editMode ? (
				<Table.NumberInputColumn
					accessorKey={'revenue'}
					header={formatMessage({id: 'deliverable.columns.revenue'})}
					onInputChange={recalculateRow}
					width="14%"
					format={NumberField.FORMAT.CURRENCY}
					currency={currency}
				/>
			) : (
				<Table.CurrencyColumn
					accessorKey={'revenue'}
					header={formatMessage({id: 'deliverable.columns.revenue'})}
					align={Table.Align.right}
					currency={currency}
					visible={eyeOptionMap ? eyeOptionMap['expense_estimates.revenue'] : true}
				/>
			)}
			{editMode ? (
				<Table.NumberInputColumn
					accessorKey={'cost'}
					header={formatMessage({id: 'deliverable.columns.cost'})}
					onInputChange={recalculateRow}
					width="14%"
					format={NumberField.FORMAT.CURRENCY}
					currency={currency}
				/>
			) : (
				<Table.CurrencyColumn
					accessorKey={'cost'}
					header={formatMessage({id: 'deliverable.columns.cost'})}
					align={Table.Align.right}
					currency={currency}
					visible={eyeOptionMap ? eyeOptionMap['expense_estimates.cost'] : true}
				/>
			)}
			{editMode ? (
				<Table.NumberInputColumn
					accessorKey={'markup'}
					header={formatMessage({id: 'deliverable.columns.markup'})}
					onInputChange={recalculateRow}
					width="14%"
					format={NumberField.FORMAT.PERCENT}
				/>
			) : (
				<Table.PercentageColumn
					accessorKey={'markup'}
					header={formatMessage({id: 'deliverable.columns.markup'})}
					align={Table.Align.right}
					visible={eyeOptionMap ? eyeOptionMap['expense_estimates.markup'] : true}
				/>
			)}
			<Table.CurrencyColumn
				accessorKey={'profit'}
				header={formatMessage({id: 'deliverable.columns.profit'})}
				align={Table.Align.right}
				currency={currency}
				width="14%"
				visible={eyeOptionMap ? eyeOptionMap['expense_estimates.profit'] : true}
			/>
			<Table.PercentageColumn
				accessorKey={'margin'}
				header={formatMessage({id: 'deliverable.columns.margin'})}
				align={Table.Align.right}
				width="14%"
				visible={eyeOptionMap ? eyeOptionMap['expense_estimates.margin'] : true}
			/>
			<Table.ActionColumn header={'contextmenu'} hideHeader hidden={!editMode}>
				<Table.ActionColumn.Option data-cy={'delete-baseline-expense'} onClick={onDeleteRow}>
					{formatMessage({id: 'deliverable.columns.delete_expense_estimate'})}
				</Table.ActionColumn.Option>
			</Table.ActionColumn>
		</Table>
	);
});
